import { api, downloadAuthorized } from './client';
import type { ClientUser } from '../stores/auth';

export const login = (email: string, password: string) =>
  api.post<{ access_token: string; expires_at: string; client: ClientUser }>('/client/v1/auth/login', { email, password });

export const refreshSession = () =>
  api.post<{ access_token: string; expires_at: string }>('/client/v1/auth/refresh');

export type AllowedCompetition = { id: string; name: string; code: string };

export type AllowedTeam = { id: string; name: string; short_name?: string; code?: string };

export type ClientMe = ClientUser & {
  allowed_sports: string[];
  allowed_origins: string[];
  allowed_competition_ids: string[];
  allowed_competitions?: AllowedCompetition[];
  allowed_team_ids?: string[];
  allowed_teams?: AllowedTeam[];
  parent_client_id?: string | null;
  parent_company_name?: string | null;
  delivery_delay_ms: number;
  max_qps: number;
  timezone?: string;
  quiet_hours_enabled?: boolean;
  quiet_hours_start?: string | null;
  quiet_hours_end?: string | null;
  morning_pack_enabled?: boolean;
  morning_pack_hour?: number;
  onboarding_completed_at?: string | null;
  widget_powered_by?: boolean;
  sponsor_logo_url?: string | null;
  sponsor_label?: string | null;
  portal_kind?: 'league' | 'club' | 'press' | string;
  public_slug?: string | null;
  auto_round_pack?: boolean;
  auto_club_digest?: boolean;
  sponsor_report_email?: string | null;
  wire_plan?: 'off' | 'flash' | 'match' | 'season';
  wire_auto?: boolean;
  onair_enabled?: boolean;
  line_plan?: 'off' | 'feed' | 'live';
  product_pack?: string;
  pbp_cap?: 'T1' | 'T2' | 'T3';
  entitlements?: {
    pack?: string;
    pbp_cap?: string;
    wire_plan?: string;
    onair?: boolean;
    line_plan?: string;
    clips?: boolean;
    office?: boolean;
    hall?: boolean;
    social?: boolean;
    scouting?: boolean;
    white_label?: boolean;
  };
};

export const me = () => api.get<ClientMe>('/client/v1/me');
export const patchMe = (body: {
  allowed_origins?: string[];
  company_name?: string;
  contact_email?: string;
  current_password?: string;
  new_password?: string;
  timezone?: string;
  quiet_hours_enabled?: boolean;
  quiet_hours_start?: string | null;
  quiet_hours_end?: string | null;
  morning_pack_enabled?: boolean;
  morning_pack_hour?: number;
  sponsor_logo_url?: string | null;
  sponsor_label?: string | null;
  widget_powered_by?: boolean;
  public_slug?: string | null;
  auto_round_pack?: boolean;
  auto_club_digest?: boolean;
  sponsor_report_email?: string | null;
  wire_plan?: 'off' | 'flash' | 'match' | 'season';
  wire_auto?: boolean;
  onair_enabled?: boolean;
  line_plan?: 'off' | 'feed' | 'live';
}) => api.patch<ClientMe>('/client/v1/me', body);
export const previewToken = () =>
  api.post<{ key: string; expires_at: string }>('/client/v1/preview-token');

export type UsageDay = { day: string; requests: number };
export type FirstHour = {
  window_started_at: string;
  account_created_at: string;
  requests: number;
  webhook_ok: number;
  webhook_failed: number;
  last_delivery_at?: string | null;
};

export type OnboardingState = {
  kind?: 'league' | 'club';
  completed: boolean;
  ready?: boolean;
  onboarding_completed_at?: string | null;
  steps: {
    site: { done: boolean; origins_count: number };
    webhook: { done: boolean; count: number };
    sandbox: { done: boolean; replays: number };
    clubs?: { done: boolean; count: number; target: number };
    replay_per_destination?: { done: boolean; destinations: number; replayed: number };
    white_label?: { done: boolean; powered_by: boolean; has_sponsor: boolean };
    widget?: { done: boolean; keys: number };
    telegram?: { done: boolean; count: number };
  };
  first_hour?: FirstHour;
};

export type Usage = {
  max_qps: number;
  delivery_delay_ms: number;
  requests_today: number;
  days: UsageDay[];
  keys_active: number;
  keys_used_today: number;
  near_limit: boolean;
  first_hour?: FirstHour;
  onboarding?: OnboardingState;
  impressions?: {
    days: number;
    mounts: number;
    views: number;
    by_widget: { widget: string; event: string; count: number }[];
    by_club: { client_id: string; company_name: string; count: number }[];
  };
};
export const getOnboarding = () => api.get<OnboardingState>('/client/v1/onboarding');
export const completeOnboarding = () => api.post<OnboardingState>('/client/v1/onboarding/complete');

export type SandboxGoal = {
  id: string;
  match_id: string;
  event_type: string;
  minute?: number | null;
  player: string;
  team: string;
  home: string;
  away: string;
  scheduled_at: string;
  status: string;
  label: string;
};
export const listSandboxGoals = (matchId?: string) =>
  api.get<SandboxGoal[]>(matchId ? `/client/v1/sandbox/goals?match_id=${encodeURIComponent(matchId)}` : '/client/v1/sandbox/goals');
export const getUsage = () => api.get<Usage>('/client/v1/usage');

export type PortalMatch = {
  id: string;
  home: string;
  away: string;
  home_logo_url?: string | null;
  away_logo_url?: string | null;
  competition: string;
  competition_code: string;
  sport_code: string;
  scheduled_at: string;
  status: string;
  period: string;
  score_home: number;
  score_away: number;
  coverage?: string;
  last_event_at?: string;
  last_sequence?: number;
  round_name?: string;
  matchweek?: number;
  official?: boolean;
  official_at?: string;
  qc_holds?: number;
};

export const listMatches = (opts?: { for?: 'highlights' }) => {
  const q = opts?.for ? `?for=${encodeURIComponent(opts.for)}` : '';
  return api.get<PortalMatch[]>(`/client/v1/matches${q}`);
};

export type FeedStatus = {
  live_count: number;
  live_matches: {
    id: string;
    home: string;
    away: string;
    status: string;
    scout?: string;
    last_event_at?: string;
    last_event_type?: string;
    qc_holds: number;
  }[];
  scout_on_duty?: string | null;
  last_event?: { type: string; match: string; at?: string } | null;
  qc_holds: number;
  quiet: { active: boolean; until?: string; reels_queued: number; enabled: boolean };
};

export const getFeedStatus = () => api.get<FeedStatus>('/client/v1/feed-status');

export type MatchReportEvent = {
  id: string;
  event_type: string;
  minute?: number | null;
  period?: string;
  player: string;
  team: string;
};

export type MatchReport = {
  id: string;
  home: string;
  away: string;
  home_logo_url?: string | null;
  away_logo_url?: string | null;
  competition: string;
  sport_code: string;
  status: string;
  score_home: number;
  score_away: number;
  scheduled_at: string;
  round_name?: string | null;
  matchweek?: number | null;
  official?: boolean;
  official_at?: string | null;
  scorers: MatchReportEvent[];
  seven_meters: MatchReportEvent[];
  exclusions: MatchReportEvent[];
  shot_map: string;
};

export const getMatchReport = (id: string) => api.get<MatchReport>(`/client/v1/matches/${id}/report`);
export const publishOfficial = (id: string) =>
  api.post<{ id: string; official: boolean; official_at: string }>(`/client/v1/matches/${id}/publish`);

export type RoundSummary = {
  key: string;
  matchweek: number;
  round_name: string;
  label: string;
  matches: number;
  from: string;
  to: string;
};

export type RoundDetail = {
  key: string;
  label: string;
  matches: {
    id: string;
    home: string;
    away: string;
    score_home: number;
    score_away: number;
    status: string;
    scheduled_at: string;
    official?: boolean;
    scorers: MatchReportEvent[];
    seven_meters: MatchReportEvent[];
    exclusions: MatchReportEvent[];
    shot_map: string;
  }[];
  scorers: MatchReportEvent[];
  seven_meters: MatchReportEvent[];
  exclusions: MatchReportEvent[];
};

export const listRounds = () => api.get<RoundSummary[]>('/client/v1/rounds');
export const getRound = (key: string) => api.get<RoundDetail>(`/client/v1/rounds/${encodeURIComponent(key)}`);

export type AwardCandidate = {
  person_id: string;
  player: string;
  team: string;
  goals: number;
  seven_meter_goals: number;
  avg_rating: number;
  clips: number;
  score?: number;
  match_id: string;
  copy?: string;
};

export const downloadRoundPack = (key: string) =>
  downloadAuthorized(`/client/v1/rounds/${encodeURIComponent(key)}/pack`, `kolo-${key}.zip`);

export const emailRoundPack = (key: string, to?: string) =>
  api.post<{ ok: boolean; to: string; filename?: string; label?: string }>(
    `/client/v1/rounds/${encodeURIComponent(key)}/pack/email`,
    to ? { to } : {},
  );

export const downloadSavez = (key: string, format: 'zip' | 'xml' | 'csv' = 'zip') =>
  downloadAuthorized(
    `/client/v1/rounds/${encodeURIComponent(key)}/savez?format=${format}`,
    `savez-${key}.${format}`,
  );

export const getPlayerOfRound = (key: string) =>
  api.get<{
    round: string;
    candidates: AwardCandidate[];
    published?: { player: string; team?: string; copy: string; reel_id?: string } | null;
  }>(`/client/v1/rounds/${encodeURIComponent(key)}/player-of-round`);

export const publishPlayerOfRound = (
  key: string,
  body?: { person_id?: string; match_id?: string; reel_id?: string },
) =>
  api.post<{ ok: boolean; player: string; team?: string; copy: string; reel_id?: string }>(
    `/client/v1/rounds/${encodeURIComponent(key)}/player-of-round`,
    body ?? {},
  );

export const sharePlayerOfRound = (
  key: string,
  body?: { webhook_id?: string; mode?: 'now' | 'morning' },
) =>
  api.post<{ ok?: boolean; mode: string; release_at?: string; copy?: string; error?: string }>(
    `/client/v1/rounds/${encodeURIComponent(key)}/player-of-round/share`,
    body ?? {},
  );

export type PortalKey = {
  id: string;
  prefix: string;
  display_last4: string;
  label: string;
  kind: string;
  is_active: boolean;
};

export const listKeys = () => api.get<PortalKey[]>('/client/v1/api-keys');
export const issueWidgetKey = (label: string, env: 'live' | 'test' = 'live') =>
  api.post<{ id: string; key: string; prefix: string; display_last4: string; label: string; kind: string; warning: string }>(
    '/client/v1/api-keys',
    { label, env },
  );
export const revokeKey = (id: string) => api.delete<void>(`/client/v1/api-keys/${id}`);

export type DestKind =
  | 'http' | 'slack' | 'telegram' | 'email' | 's3' | 'ftp' | 'folder' | 'dropbox'
  | 'instagram' | 'facebook' | 'whatsapp';

export type PortalWebhook = {
  id: string;
  url: string;
  secret_hint: string;
  is_active: boolean;
  created_at: string;
  event_filter?: string[];
  match_filter?: string[];
  kind?: DestKind;
  media_only?: boolean;
  paused_at?: string | null;
  paused_reason?: string | null;
};

export type DestHealthStatus = 'healthy' | 'degraded' | 'failing' | 'paused' | 'disabled' | 'idle';
export type DestinationHealth = {
  id: string;
  kind: DestKind;
  url: string;
  is_active: boolean;
  paused_at: string | null;
  paused_reason: string | null;
  attempts_24h: number;
  ok_24h: number;
  failed_24h: number;
  success_rate: number | null;
  latency_p50_ms: number | null;
  latency_p95_ms: number | null;
  last_ok_at: string | null;
  last_error_at: string | null;
  last_error: string | null;
  status: DestHealthStatus;
};
export type DeliveryHealth = {
  window: string;
  destinations: DestinationHealth[];
  ingest: { events_24h: number; latency_p50_ms: number | null; latency_p95_ms: number | null };
  generated_at: string;
};
export const getDeliveryHealth = () => api.get<DeliveryHealth>('/client/v1/delivery-health');
export const resumeWebhook = (id: string) =>
  api.post<{ id: string; is_active: boolean }>(`/client/v1/webhooks/${id}/resume`);

export type CreateWebhookBody = {
  url: string;
  event_filter?: string[];
  kind?: DestKind;
  media_only?: boolean;
  config?: Record<string, string>;
};

export const listWebhooks = () => api.get<PortalWebhook[]>('/client/v1/webhooks');
export const createWebhook = (body: CreateWebhookBody) =>
  api.post<{ id: string; secret: string; warning: string; event_filter?: string[]; kind?: DestKind; media_only?: boolean }>(
    '/client/v1/webhooks',
    body,
  );
export const deleteWebhook = (id: string) => api.delete<void>(`/client/v1/webhooks/${id}`);
export const patchWebhook = (id: string, body: Partial<CreateWebhookBody> & { is_active?: boolean }) =>
  api.patch<{ id: string; ok: boolean }>(`/client/v1/webhooks/${id}`, body);
export const rotateWebhookSecret = (id: string) =>
  api.post<{ id: string; secret: string; warning: string }>(`/client/v1/webhooks/${id}/rotate-secret`);

export type DeferredDelivery = {
  id: string;
  subscription_id: string;
  delivery_id: string;
  event_type: string;
  match_id?: string | null;
  reason: string;
  status: string;
  release_at: string;
  created_at: string;
  url: string;
  kind: string;
};

export const listDeferredWebhooks = () => api.get<DeferredDelivery[]>('/client/v1/webhook-deferred');
export type WebhookTestResult = {
  ok: boolean;
  status?: number;
  duration_ms?: number;
  error?: string;
  kind?: string;
  event_type?: string;
  delivery_id?: string;
  revision?: number;
  payload?: unknown;
  fallback?: string;
};
export const testWebhook = (id: string, body?: { mode?: string; event_id?: string; match_id?: string }) =>
  api.post<WebhookTestResult>(`/client/v1/webhooks/${id}/test`, body ?? {});

export type WebhookDelivery = {
  id: string;
  subscription_id: string;
  delivery_id: string;
  revision: number;
  event_type: string;
  match_id?: string | null;
  status: string;
  http_status?: number | null;
  error?: string | null;
  attempt: number;
  created_at: string;
  delivered_at?: string;
  url: string;
  kind: string;
  payload?: unknown;
};

export const listWebhookDeliveries = () => api.get<WebhookDelivery[]>('/client/v1/webhook-deliveries');
export const retryWebhookDelivery = (id: string) =>
  api.post<{ ok: boolean; status?: number; error?: string }>(`/client/v1/webhook-deliveries/${id}/retry`);

export type ClubTeam = {
  id: string;
  name: string;
  short_name?: string;
  code?: string;
  sport_code?: string;
  logo_url?: string;
};

export type ClubPortal = {
  id: string;
  company_name: string;
  contact_email: string;
  allowed_team_ids: string[];
  allowed_teams?: AllowedTeam[];
  allowed_origins: string[];
  has_portal_password: boolean;
  status?: string;
  club_media?: boolean;
  created_at: string;
};

export type CreateClubBody = {
  company_name: string;
  contact_email: string;
  allowed_team_ids: string[];
  allowed_origins?: string[];
  portal_password?: string;
  telegram_chat_id?: string;
  telegram_bot_token?: string;
};

export type CreatedClub = ClubPortal & {
  parent_client_id: string;
  portal_password: string;
  telegram_webhook_id?: string | null;
  warning?: string;
};

export const listClubs = () => api.get<ClubPortal[]>('/client/v1/clubs');
export const listClubTeams = () => api.get<ClubTeam[]>('/client/v1/club-teams');
export const createClub = (body: CreateClubBody) =>
  api.post<CreatedClub>('/client/v1/clubs', body);
export const patchClub = (id: string, body: {
  company_name?: string;
  allowed_team_ids?: string[];
  allowed_origins?: string[];
  status?: 'active' | 'suspended';
  portal_password?: string;
  club_media?: boolean;
}) => api.patch<{ id: string; ok: boolean; portal_password?: string; warning?: string }>(`/client/v1/clubs/${id}`, body);
export const getClubMedia = () => api.get<{
  seats: number;
  club_price_eur: number;
  origo_fee_eur: number;
  league_invoice_eur: number;
  origo_invoice_eur: number;
}>('/client/v1/club-media');
export const resetClubPassword = (id: string, portal_password?: string) =>
  api.post<{ id: string; portal_password: string; warning: string }>(`/client/v1/clubs/${id}/password`, { portal_password });
export const getClubEmbedPack = (id: string) =>
  api.get<{
    club_id: string;
    company_name: string;
    team_id: string;
    team_name: string;
    team_code: string;
    competition_code: string;
    sport: string;
    widgets: string[];
    html: string;
  }>(`/client/v1/clubs/${id}/embed-pack`);

export const getMyEmbedPack = () =>
  api.get<{
    club_id: string;
    company_name: string;
    team_id: string;
    team_name: string;
    team_code: string;
    team_logo_url?: string;
    calendar_url?: string;
    competition_code: string;
    sport: string;
    widgets: string[];
    html: string;
  }>('/client/v1/me/embed-pack');

export const getMySquad = () =>
  api.get<{ players: { person_id: string; full_name: string; role: string; shirt_number?: number; is_captain?: boolean; photo_url?: string }[] }>(
    '/client/v1/me/squad',
  );

export type TeamColors = { primary?: string; secondary?: string };
export type PendingTeamLogo = {
  url: string;
  colors?: TeamColors;
  club_id: string;
  club_name: string;
  submitted_at: string;
};

export const getMyTeamBranding = () =>
  api.get<{
    team_id: string;
    team_name: string;
    logo_url?: string | null;
    colors: TeamColors;
    pending: PendingTeamLogo | null;
  }>('/client/v1/me/team-branding');

export function submitMyTeamLogo(file: File, colors: { primary: string; secondary: string }) {
  const form = new FormData();
  form.append('file', file);
  form.append('primary', colors.primary);
  form.append('secondary', colors.secondary);
  return api.postForm<{ team_id: string; status: 'pending'; pending: PendingTeamLogo }>('/client/v1/me/team-logo', form);
}

export type TeamLogoRequest = {
  team_id: string;
  team_name: string;
  logo_url?: string | null;
  pending: PendingTeamLogo;
};

export const listTeamLogoRequests = () => api.get<TeamLogoRequest[]>('/client/v1/team-logo-requests');
export const approveTeamLogoRequest = (teamId: string) =>
  api.post<{ team_id: string; logo_url: string }>(`/client/v1/team-logo-requests/${teamId}/approve`);
export const rejectTeamLogoRequest = (teamId: string) =>
  api.post<{ team_id: string; rejected: boolean }>(`/client/v1/team-logo-requests/${teamId}/reject`);

export const shareHighlight = (id: string, body?: { webhook_id?: string; mode?: 'now' | 'morning' }) =>
  api.post<{ ok?: boolean; mode: string; release_at?: string; share_url?: string; caption?: string; error?: string; kind?: string }>(
    `/client/v1/highlights/${id}/share`,
    body ?? {},
  );

export const listAwards = () =>
  api.get<{ round: string; player: string; team?: string; copy?: string; reel_id?: string }[]>('/client/v1/awards');

export type SponsorReport = {
  month: string;
  company_name: string;
  sponsor_logo_url?: string | null;
  sponsor_label?: string | null;
  mounts: number;
  views: number;
  by_club: { name: string; count: number }[];
  by_round: { name: string; count: number }[];
  by_widget: { widget: string; event: string; count: number }[];
  slots?: { round_key: string; sponsor_label: string; logo_url: string; report_email?: string }[];
};

export const getSponsorReport = (month: string) =>
  api.get<SponsorReport>(`/client/v1/sponsor-report?month=${encodeURIComponent(month)}`);

export const printSponsorReport = (month: string) =>
  downloadAuthorized(`/client/v1/sponsor-report?month=${encodeURIComponent(month)}&format=html`, `sponsor-${month}.html`);

export const emailSponsorReport = (month: string, to?: string) =>
  api.post<{ ok: boolean; to: string; month: string }>('/client/v1/sponsor-report/email', { month, to });

export type SponsorSlot = {
  id: string;
  round_key: string;
  sponsor_label: string;
  logo_url: string;
  notes?: string;
  report_email?: string;
  goal_sponsor?: boolean;
  created_at: string;
};

export const listSponsorSlots = () => api.get<SponsorSlot[]>('/client/v1/sponsor-slots');
export const createSponsorSlot = (body: {
  round_key: string;
  logo_url: string;
  sponsor_label?: string;
  notes?: string;
  report_email?: string;
  goal_sponsor?: boolean;
}) => api.post<SponsorSlot>('/client/v1/sponsor-slots', body);
export const getGoalSponsorReport = (roundKey: string) =>
  api.get<{ round_key: string; sponsor_label: string; goal_sponsor: boolean; goals: number; surfaces: string[] }>(
    `/client/v1/sponsor-slots/report?round_key=${encodeURIComponent(roundKey)}`,
  );
export const deleteSponsorSlot = (id: string) => api.delete<void>(`/client/v1/sponsor-slots/${id}`);

export type EmbedLicense = {
  id: string;
  site_name: string;
  origin: string;
  notes?: string;
  active: boolean;
  created_at: string;
};
export const listEmbedLicenses = () => api.get<{
  licenses: EmbedLicense[];
  active: number;
  included: number;
  extra: number;
  origo_fee_eur: number;
  origo_invoice_eur: number;
}>('/client/v1/embed-licenses');
export const createEmbedLicense = (body: { site_name: string; origin: string; notes?: string }) =>
  api.post<{ origin: string; site_name: string; note: string }>('/client/v1/embed-licenses', body);
export const revokeEmbedLicense = (id: string) =>
  api.post<{ revoked: boolean }>(`/client/v1/embed-licenses/${id}/revoke`);

export type PlayerCard = {
  id: string;
  person_id: string;
  full_name: string;
  sponsor_label: string;
  logo_url?: string;
  page_url: string;
};
export const listPlayerCards = () => api.get<PlayerCard[]>('/client/v1/player-cards');
export const searchLeaguePlayers = (q: string) =>
  api.get<{ players: { person_id: string; full_name: string }[] }>(`/client/v1/player-cards?q=${encodeURIComponent(q)}`);
export const savePlayerCard = (body: { person_id: string; sponsor_label: string; logo_url?: string }) =>
  api.post<{ page_url: string }>('/client/v1/player-cards', body);
export const deletePlayerCard = (personId: string) =>
  api.delete<void>(`/client/v1/player-cards/${personId}`);

export const listPress = () =>
  api.get<{ id: string; company_name: string; contact_email: string }[]>('/client/v1/press');
export const createPress = (body: { company_name: string; contact_email: string; portal_password?: string }) =>
  api.post<{ id: string; contact_email: string; portal_password: string }>('/client/v1/press', body);
export const resetPressPassword = (id: string) =>
  api.post<{ portal_password: string }>(`/client/v1/press/${id}/password`);

export type ScoutingReport = {
  team_name: string;
  seven_meter_pct: number;
  seven_meter_goals: number;
  seven_meter_attempts: number;
  two_minute: number;
  nine_meter: number;
  matches: { id: string; home: string; away: string; score_home: number; score_away: number }[];
  nine_meter_shooters: { player: string; nine_meter: number }[];
  shots: { id: string; x?: number; y?: number; event_type: string }[];
  clips?: { id: string; player?: string; event_type: string; kind: string; minute?: number; preview_url?: string; rating: number }[];
};

export const getScouting = (teamId: string) => api.get<ScoutingReport>(`/client/v1/scouting/${teamId}`);

export const listDiscipline = (round?: string) =>
  api.get<{ incidents: {
    id: string;
    match_id: string;
    home: string;
    away: string;
    player: string;
    team?: string;
    event_type: string;
    minute?: number;
    clip_url?: string;
  }[] }>(
    round ? `/client/v1/discipline?round=${encodeURIComponent(round)}` : '/client/v1/discipline',
  );
export const downloadDisciplinePack = (round?: string) =>
  downloadAuthorized(
    round ? `/client/v1/discipline/pack?round=${encodeURIComponent(round)}` : '/client/v1/discipline/pack',
    'disciplina.zip',
  );

export type Venue = { id: string; name: string; origin?: string; delay_ms: number };
export const listVenues = () => api.get<Venue[]>('/client/v1/venues');
export const createVenue = (body: { name: string; origin?: string; delay_ms?: number }) =>
  api.post<Venue>('/client/v1/venues', body);
export const deleteVenue = (id: string) => api.delete<void>(`/client/v1/venues/${id}`);
export const getVenuePack = (id: string) => api.get<{ html: string }>(`/client/v1/venues/${id}/pack`);

export type EmbedHealth = {
  origins: string[];
  missing: string[];
  ok: boolean;
  sites: {
    origin: string;
    widget: string;
    state: 'live' | 'seen' | 'stale' | 'never';
    last_mount_at?: string | null;
    mounts: number;
    views: number;
    age?: string;
  }[];
};
export const getEmbedHealth = () => api.get<EmbedHealth>('/client/v1/embed-health');

export type StandingRow = {
  rank: number;
  team_id: string;
  team_name: string;
  played: number;
  points: number;
  won?: number;
  drawn?: number;
  lost?: number;
};
export type HistoryPayload = {
  season_id?: string;
  seasons: {
    id: string;
    name: string;
    current: boolean;
    competition: string;
    competition_code: string;
    start_date?: string;
  }[];
  standings?: { season_name: string; rows: StandingRow[] };
  previous?: { season_name: string; rows: StandingRow[] };
  yoy?: { team_name: string; rank: number; prev_rank?: number; rank_delta?: number; points: number; prev_points?: number }[];
};
export const getHistory = (seasonId?: string) =>
  api.get<HistoryPayload>(seasonId ? `/client/v1/history?season_id=${encodeURIComponent(seasonId)}` : '/client/v1/history');

export const getPlayerCareer = (id: string) =>
  api.get<{ player: string; seasons: { season: string; competition_code: string; goals: number; seven_meter: number; two_minute: number; matches: number }[] }>(
    `/client/v1/players/${id}/career`,
  );

export type PressKit = {
  id: string;
  home: string;
  away: string;
  score_home: number;
  score_away: number;
  official?: boolean;
  story: string;
  url: string;
  clips?: { id: string; event_type: string; play_url: string }[];
};
export const getPressKit = (id: string) => api.get<PressKit>(`/client/v1/matches/${id}/press-kit`);
export const emailPressKit = (id: string, to?: string) =>
  api.post<{ ok: boolean; to: string; url: string }>(`/client/v1/matches/${id}/press-kit/email`, to ? { to } : {});

export type WireDrop = {
  id: string;
  kind: 'flash' | 'match' | 'round' | string;
  status: string;
  media_late?: boolean;
  overdue?: boolean;
  dest?: string;
  error?: string | null;
  match_id?: string | null;
  promised_at?: string;
  delivered_at?: string;
  created_at?: string;
  card?: {
    copy?: { telegram?: string; press_lead?: string };
    match?: { home?: string; away?: string; score_home?: number; score_away?: number };
    links?: { wire?: string; card?: string; obs?: string };
  };
};

export type WireInbox = {
  plan: string;
  auto: boolean;
  slug?: string;
  wire_url?: string;
  obs_url?: string;
  drops: WireDrop[];
  sla: { overdue: number; late_media: number };
};

export const getWire = () => api.get<WireInbox>('/client/v1/wire');
export const sendWire = (id: string) => api.post<WireDrop>(`/client/v1/wire/${id}/send`, {});
export const buildMatchWire = (matchId: string) => api.post<WireDrop>(`/client/v1/wire/matches/${matchId}`, {});
export const buildRoundWire = (key: string) => api.post<WireDrop>(`/client/v1/wire/rounds/${key}`, {});

export type OnAirMatch = {
  id: string;
  home: string;
  away: string;
  status: string;
  score_home: number;
  score_away: number;
  delay_ms: number;
  scheduled_at: string;
  onair_url: string;
  obs_url: string;
  story_url?: string;
  receipt_url?: string;
  demo_url?: string;
};
export const listOnAir = () => api.get<{ matches: OnAirMatch[] }>('/client/v1/onair');

export type LineDrop = {
  id: string;
  kind: string;
  media_late?: boolean;
  created_at?: string;
  league?: string;
  slug?: string;
  card?: { copy?: { telegram?: string; press_lead?: string } };
};
export type LineFeed = {
  plan: string;
  version: string;
  count: number;
  drops: LineDrop[];
  stream_url?: string;
};
export const getLine = (since?: string) =>
  api.get<LineFeed>(since ? `/client/v1/line?since=${encodeURIComponent(since)}` : '/client/v1/line');

